import { useState, useEffect } from "react";
import { DateRange } from "react-day-picker";
import { DateRangePicker } from "@/components/DateRangePicker";
import { ProfitChart } from "@/components/ProfitChart";
import { StatsCard } from "@/components/StatsCard";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "@/i18n/useTranslation";
import { AppRole } from "@/utils/routePermissions";

const mockData = [
  { date: "2024-01", profit: 4000 },
  { date: "2024-02", profit: 3000 },
  { date: "2024-03", profit: 2000 },
  { date: "2024-04", profit: 2780 },
  { date: "2024-05", profit: 1890 },
  { date: "2024-06", profit: 2390 },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(2024, 0, 1),
    to: new Date(),
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/auth");
          return;
        }

        // Check if user has manager role or higher to be on this page
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id);
        
        if (rolesError) throw rolesError;
        
        const roles = userRoles?.map(r => r.role as AppRole) || [];
        if (!roles.some(role => ['owner', 'admin', 'manager'].includes(role))) {
          // If user is not manager or higher, redirect to board
          navigate("/board");
          return;
        }

        const { data: profile, error } = await supabase
          .from("users")
          .select("company_id")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (error) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to fetch user profile. Please try again.",
          });
          return;
        }

        if (!profile?.company_id) {
          navigate("/auth");
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Manager Dashboard</h1>
          <DateRangePicker date={date} onDateChange={setDate} />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Total Profit"
            value="$45,231.89"
            description="Total profit across all employees"
          />
          <StatsCard
            title="Average Profit per Employee"
            value="$15,077.30"
            description="Average profit per employee"
          />
          <StatsCard
            title="Top Performing Employee"
            value="John Doe"
            description="$20,154.59 in profit"
          />
        </div>

        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-4 text-lg font-semibold">Profit Over Time</h2>
          <ProfitChart data={mockData} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
